import { FlagDefinitionApi } from '@app/api';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { ClassModalityLabel, ClassStatusLabel, ClassTypeLabel } from '@app/components/KlassLabels';
import { NotFoundState } from '@app/components/NotFoundState';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useEntity } from '@app/hooks/useEntity';
import { FLAG_LEVELS_BY_SEVERITY, FlagDefinition, FlagLevel, FlagTargetEntity, getFlagLevelConfig } from '@app/model/Flag';
import { useFlagDefinitionStore } from '@app/store/entityStore';
import {
  ActionGroup,
  AlertVariant,
  Button,
  Card, CardBody,
  EmptyState, EmptyStateBody,
  Form, FormGroup,
  FormSelect, FormSelectOption,
  Modal,
  ModalBody, ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Spinner,
  Switch,
  TextInput
} from '@patternfly/react-core';
import { HelpIcon, SearchIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RuleEditor } from './RuleEditor';
import { RuleHelpModal } from './RuleHelpModal';

// --- SUB-COMPONENTS FOR PREVIEW TABLES ---

// Helper function to capitalize the first letter of column titles
const formatColumnTitle = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

// 1. The specifically formatted KLASS preview table
const KlassPreviewTable: React.FC<{ data: any[] }> = ({ data }) => {
  const columns = data.length > 0 
    ? Object.keys(data[0]).filter(key => key !== 'id') 
    : [];
  
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table aria-label="Klass Preview Results" variant="compact">
        <Thead>
          <Tr>
            {columns.map((colName) => (
              <Th key={colName}>{formatColumnTitle(colName)}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, rowIndex) => (
            <Tr key={rowIndex}>
              {columns.map((colName) => {
                const cellValue = row[colName];
                let displayValue: React.ReactNode = String(cellValue || 'N/A');

                // Apply specific formatting based on the column name
                if (colName === 'type') {
                  displayValue = <ClassTypeLabel type={cellValue} />;
                } else if (colName === 'modality') {
                  displayValue = <ClassModalityLabel modality={cellValue} />;
                } else if (colName === 'status') {
                  displayValue = <ClassStatusLabel status={cellValue} />;
                } else if (colName === 'sku') {
                  displayValue = <strong>{cellValue}</strong>;
                } else if (typeof cellValue === 'object' && cellValue !== null) {
                  displayValue = JSON.stringify(cellValue);
                }

                return (
                  <Td key={`${rowIndex}-${colName}`} dataLabel={colName}>
                    {displayValue}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </div>
  );
};

// 2. The generic fallback table for INSTRUCTOR or future entities
const GenericPreviewTable: React.FC<{ data: any[] }> = ({ data }) => {
  const columns = data.length > 0 ? Object.keys(data[0]) : [];
  
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table aria-label="Generic Preview Results" variant="compact">
        <Thead>
          <Tr>
            {columns.map((colName) => (
              <Th key={colName}>{formatColumnTitle(colName)}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, rowIndex) => (
            <Tr key={rowIndex}>
              {columns.map((colName) => {
                const cellValue = row[colName];
                const displayValue = typeof cellValue === 'object' && cellValue !== null 
                  ? JSON.stringify(cellValue) 
                  : String(cellValue || 'N/A');
                  
                return (
                  <Td key={`${rowIndex}-${colName}`} dataLabel={colName}>
                    {displayValue}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </div>
  );
};

// --- MAIN FLAG DETAIL COMPONENT ---

export const FlagDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  
  const [isValid, setIsValid] = React.useState(true);
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);
  
  const [flag, setFlag] = React.useState<Partial<FlagDefinition>>({
    targetEntity: FlagTargetEntity.KLASS, 
    level: FlagLevel.ALERT, 
    enabled: true 
  });

  const [isHelpOpen, setIsHelpOpen] = React.useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = React.useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const [previewData, setPreviewData] = React.useState<any[]>([]);

  // --- Cache Connection ---
  const { items: flags, isLoaded, retry } = useEntity(useFlagDefinitionStore);
  const saveFlagDef = useFlagDefinitionStore(state => state.save);

  // --- Populate Form on Load ---
  React.useEffect(() => {
    if (id && id !== 'new' && isLoaded) {
      // Find the specific flag in our cache by ID (cast to string for safe comparison)
      const existingFlag = flags.find(f => String(f.id) === id);
      if (existingFlag) setFlag(existingFlag);
    }
  }, [id, isLoaded, flags]);

  /**
   * The rule is read out of the loaded list rather than fetched by id, so "not found" is the
   * list having arrived without it - which is also why it waits on `isLoaded`: before that,
   * every id is absent merely because nothing has loaded yet.
   */
  const notFound = Boolean(id) && id !== 'new' && isLoaded && !flags.some(f => String(f.id) === id);

  const onSave = async () => {
    const isNew = id === 'new' || !flag.id;
    
    try {
      // The store handles POST vs PUT automatically
      await saveFlagDef(flag, isNew);
      
      addNotification(`Rule successfully ${isNew ? 'created' : 'updated'}!`, AlertVariant.success);
      // Same state the layout's Back button carries, so the list comes back filtered as it was
      navigate('/admin/flags', { state: { restoreFilters: true } });
    } catch (error: any) {
      console.error("Save error:", error);
      addNotification(`Failed to save rule: ${error.message || 'Unknown error occurred'}`, AlertVariant.danger);
    }
  };

  const handlePreview = async () => {
    setIsPreviewModalOpen(true);
    setIsPreviewLoading(true);
    setPreviewData([]);

    try {
      // Clean, strongly-typed, and URL-free
      const data = await FlagDefinitionApi.preview(flag.definition || '', flag.targetEntity || '');
      setPreviewData(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error("Preview error:", error);
      addNotification(`Preview failed: ${error.message || 'Invalid syntax'}`, AlertVariant.danger);
      setIsPreviewModalOpen(false); 
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const jexlHelpIcon = (
    <button
      type="button"
      aria-label="Open the rule language guide"
      onClick={() => setIsHelpOpen(true)}
      className="pf-v6-c-form__group-label-help"
    >
      <HelpIcon />
    </button>
  );

  if (notFound) {
    return (
      <NotFoundState
        entityLabel="rule"
        identifier={id}
        backTo={{ path: '/admin/flags', label: 'Back to flags' }}
      />
    );
  }

  const changeLogTitle = flag.name;
  /**
   * Offered only once the record is stored: a form that has never been saved has no revision to
   * read, and the endpoint would answer 404 for the id it does not yet have.
   */
  const changeLogItems = flag.id
    ? [{ key: 'change-log', label: 'Change log', onClick: () => setChangeLogOpen(true) }]
    : [];


  return (
    <EntityChangeLog
      entity="flagdefinition"
      id={flag.id}
      entityLabel="this rule"
      title={changeLogTitle}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      {/* Kept at xl: a rule is a row of a table one level in, not a record of its own like a
          class or an instructor, and the standard 4xl reads as one here. */}
      <DetailPageHeader
        title={id === 'new' ? 'Create Rule' : 'Edit Rule'}
        titleSize="xl"
        actions={<PageActionsMenu onRefresh={retry} items={changeLogItems} />}
      />

      <PageSection>
        <Card>
          <CardBody>
            <Form>
              <FormGroup label="Status" fieldId="enabled">
                <Switch 
                  id="enabled" 
                  label="Enabled" 
                  isChecked={flag.enabled} 
                  onChange={(_e, checked) => setFlag({...flag, enabled: checked})} 
                />
              </FormGroup>

              <FormGroup label="Name" isRequired fieldId="name">
                <TextInput value={flag.name || ''} onChange={(_e, v) => setFlag({...flag, name: v})} isRequired id="name" />
              </FormGroup>
              
              <FormGroup label="Description" fieldId="description">
                <TextInput value={flag.description || ''} onChange={(_e, v) => setFlag({...flag, description: v})} id="description" />
              </FormGroup>

              <FormGroup label="Target Entity" isRequired fieldId="target">
                <FormSelect value={flag.targetEntity} onChange={(_e, v) => setFlag({...flag, targetEntity: v as any})} id="target">
                  <FormSelectOption value={FlagTargetEntity.KLASS} label="Klass" />
                  <FormSelectOption value={FlagTargetEntity.INSTRUCTOR} label="Instructor" />
                </FormSelect>
              </FormGroup>

              <FormGroup label="Level" isRequired fieldId="level">
                <FormSelect value={flag.level} onChange={(_e, v) => setFlag({...flag, level: v as any})} id="level">
                  {FLAG_LEVELS_BY_SEVERITY.map(level => (
                    <FormSelectOption key={level} value={level} label={getFlagLevelConfig(level).text} />
                  ))}
                </FormSelect>
              </FormGroup>

              <FormGroup 
                label="Rule Logic (JEXL)" 
                isRequired 
                fieldId="definition" 
                labelHelp={jexlHelpIcon}
              >
                <RuleEditor 
                  value={flag.definition || ''} 
                  onChange={(v) => setFlag({...flag, definition: v})} 
                  onValidationChange={setIsValid}
                  targetEntity={flag.targetEntity || ''}
                />
              </FormGroup>

              <ActionGroup>
                <Button variant="primary" onClick={onSave} isDisabled={!isValid || !flag.name?.trim()}>Save</Button>
                <Button variant="secondary" onClick={handlePreview} isDisabled={!isValid || !flag.definition?.trim()}>Preview Matches</Button>
                <Button variant="link" onClick={() => navigate('/admin/flags', { state: { restoreFilters: true } })}>Cancel</Button>
              </ActionGroup>
            </Form>
          </CardBody>
        </Card>
      </PageSection>

      <RuleHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />

      <Modal
        variant={ModalVariant.large}
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        aria-labelledby="preview-modal-title"
      >
        <ModalHeader title="Expression Preview (Top 10 Matches)" labelId="preview-modal-title" />
        <ModalBody>
          {isPreviewLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spinner size="xl" aria-label="Loading preview results" />
              <div style={{ marginTop: '1rem' }}>Evaluating rule on the database...</div>
            </div>
          ) : previewData.length === 0 ? (
            <EmptyState titleText="No Matches Found" icon={SearchIcon} headingLevel="h4">
              <EmptyStateBody>
                This expression is valid, but it currently does not match any {flag.targetEntity?.toLowerCase()} records in the database.
              </EmptyStateBody>
            </EmptyState>
          ) : (
            /* NEW: Render specific component based on Target Entity */
            flag.targetEntity === FlagTargetEntity.KLASS ? (
              <KlassPreviewTable data={previewData} />
            ) : (
              <GenericPreviewTable data={previewData} />
            )
          )}
        </ModalBody>
        <ModalFooter>
          <Button key="close" variant="primary" onClick={() => setIsPreviewModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </EntityChangeLog>
  );
};